<template>
  <div ref="root" class="terrain-select" :class="{ 'terrain-select--open': isOpen, 'terrain-select--disabled': disabled }">
    <button
      ref="trigger"
      type="button"
      class="terrain-select__trigger terrain-focusable"
      role="combobox"
      aria-haspopup="listbox"
      :aria-label="ariaLabel"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-controls="listboxId"
      :aria-activedescendant="isOpen ? activeOptionId : null"
      :disabled="disabled"
      @click="toggle"
      @keydown="handleKeydown"
    >
      <span class="terrain-select__value">{{ selectedLabel }}</span>
      <span class="terrain-select__chevron" aria-hidden="true">▾</span>
    </button>

    <ul v-if="isOpen" ref="list" :id="listboxId" class="terrain-select__list" role="listbox">
      <li
        v-for="(option, index) in options"
        :id="optionId(index)"
        :key="String(option.value)"
        class="terrain-select__option"
        :class="{ 'terrain-select__option--active': index === activeIndex, 'terrain-select__option--selected': option.value === value }"
        role="option"
        :aria-selected="option.value === value ? 'true' : 'false'"
        @mousedown.prevent="select(option)"
        @mouseenter="activeIndex = index"
      >{{ option.label }}</li>
    </ul>
  </div>
</template>

<script>
let idCounter = 0;

export default {
  name: 'TerrainSelect',
  props: {
    value: { type: [String, Number, Boolean], default: '' },
    options: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    ariaLabel: { type: String, default: 'Seleccionar opción' },
  },
  data() { return { id: `terrain-select-${++idCounter}`, isOpen: false, activeIndex: 0, fieldLabel: null }; },
  computed: {
    selectedIndex() { return this.options.findIndex((option) => option.value === this.value); },
    selectedLabel() {
      const option = this.options[this.selectedIndex];
      return option ? option.label : 'Seleccionar…';
    },
    listboxId() { return `${this.id}-listbox`; },
    activeOptionId() { return this.optionId(this.activeIndex); },
  },
  mounted() {
    this.fieldLabel = this.$el.closest('label');
    if (this.fieldLabel) this.fieldLabel.classList.add('terrain-select-field');
    document.addEventListener('mousedown', this.closeOnOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.closeOnOutsideClick);
    if (this.fieldLabel) this.fieldLabel.classList.remove('terrain-select-field', 'terrain-select-field--open');
  },
  watch: {
    isOpen(open) {
      if (this.fieldLabel) this.fieldLabel.classList.toggle('terrain-select-field--open', open);
    },
  },
  methods: {
    optionId(index) { return `${this.id}-option-${index}`; },
    toggle() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen) this.activeIndex = Math.max(this.selectedIndex, 0);
    },
    close() { this.isOpen = false; },
    closeOnOutsideClick(event) {
      if (this.$refs.root && !this.$refs.root.contains(event.target)) this.close();
    },
    select(option) {
      this.$emit('input', option.value);
      this.close();
      this.$nextTick(() => this.$refs.trigger && this.$refs.trigger.focus());
    },
    handleKeydown(event) {
      if (this.disabled || !this.options.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!this.isOpen) this.isOpen = true;
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = (this.activeIndex + direction + this.options.length) % this.options.length;
      } else if (event.key === 'Home') {
        event.preventDefault(); this.isOpen = true; this.activeIndex = 0;
      } else if (event.key === 'End') {
        event.preventDefault(); this.isOpen = true; this.activeIndex = this.options.length - 1;
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (this.isOpen) this.select(this.options[this.activeIndex]); else this.toggle();
      } else if (event.key === 'Escape') {
        this.close();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.terrain-select { position: relative; min-width: 0; }
.terrain-select--open { z-index: 1; }
.terrain-select__trigger {
  display: flex; align-items: center; justify-content: space-between; gap: var(--terrain-space-sm);
  width: 100%; min-height: 38px; padding: var(--terrain-space-sm) var(--terrain-space-md);
  border: 1px solid var(--color-line); border-radius: var(--terrain-radius-md); background: var(--color-white);
  color: var(--color-graphite); font: inherit; font-weight: 400; text-align: left; cursor: pointer;
  transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);
  &:hover { border-color: var(--color-steel); }
  &:focus { outline: none; border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); }
}
.terrain-select--open .terrain-select__trigger { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); }
.terrain-select--disabled .terrain-select__trigger { background: var(--color-mist); color: var(--color-ghost); cursor: not-allowed; }
.terrain-select__value { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.terrain-select__chevron { flex: 0 0 auto; color: var(--color-ghost); font-size: .72rem; transition: transform var(--terrain-transition); }
.terrain-select--open .terrain-select__chevron { transform: rotate(180deg); }
.terrain-select__list {
  position: absolute; z-index: 2; top: calc(100% + var(--terrain-space-xs)); left: 0; right: 0; box-sizing: border-box;
  max-height: 240px; margin: 0; padding: var(--terrain-space-xs); overflow-y: auto; list-style: none;
  background-color: var(--color-white) !important; background-image: none !important; color: var(--color-graphite) !important;
  opacity: 1 !important; filter: none !important; backdrop-filter: none !important; mix-blend-mode: normal;
  border: 1px solid var(--color-line); border-radius: var(--terrain-radius-lg); box-shadow: 0 12px 28px rgba(14, 42, 64, 0.24); isolation: isolate;
}
.terrain-select__option { padding: var(--terrain-space-sm) var(--terrain-space-md); border-radius: var(--terrain-radius-sm); background: var(--color-white); color: var(--color-graphite) !important; cursor: pointer; }
.terrain-select__option--active, .terrain-select__option:hover { background: var(--color-mist) !important; }
.terrain-select__option--selected { color: var(--color-navy); font-weight: 700; }
</style>

<style lang="scss">
// El estilo global existente aplica opacity: .8 a todos los label. La
// opacidad del padre se compone sobre el dropdown y no se puede anular en
// un hijo; esta clase se agrega solo a labels que contienen TerrainSelect.
label.terrain-select-field {
  opacity: 1 !important;
  position: relative;
}

// Mantiene el control abierto sobre los labels e inputs vecinos con niveles
// locales y normales, sin convertir todo el módulo en una capa gigante.
label.terrain-select-field--open { z-index: 1; }
</style>
